import { useEffect } from 'react';

const headerObserverMap: WeakMap<HTMLElement, ResizeObserver> = new WeakMap();

/**
 * Measures the site header height and writes it to CSS var `--header-h` on :root.
 * - Prefers an element with `[data-site-header]` if present, otherwise the first <header>.
 * - Uses ResizeObserver and window resize to keep it updated.
 * - Safe to call multiple times (idempotent-ish); it will reuse the same observer on the same element.
 */
export function useHeaderSize() {
  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    const root = document.documentElement;
    const setVar = (h: number) => {
      root.style.setProperty('--header-h', `${Math.max(0, Math.ceil(h))}px`);
    };

    // Fallback early to avoid CLS if delayed
    const DEFAULT_H = 64;
    if (!root.style.getPropertyValue('--header-h')) {
      setVar(DEFAULT_H);
    }

    const header = (document.querySelector('[data-site-header]') || document.querySelector('header')) as HTMLElement | null;
    if (!header) return;

    const measure = () => setVar(header.getBoundingClientRect().height || DEFAULT_H);
    measure();

    const existing = headerObserverMap.get(header);
    if (existing) existing.disconnect();
    const ro = new ResizeObserver(() => measure());
    ro.observe(header);
    headerObserverMap.set(header, ro);

    const onResize = () => measure();
    window.addEventListener('resize', onResize, { passive: true });
    return () => {
      try { ro.disconnect(); } catch { /* noop */ }
      window.removeEventListener('resize', onResize);
    };
  }, []);
}
